import { LitElement, html } from 'lit';

class SizeGuideTable extends LitElement {
  static properties = {
    columns: { type: Array },
    rows: { type: Array },
    contentCm: { type: Array },
    contentIn: { type: Array },
    convertToInches: { type: Boolean },
    hasInchesData: { type: Boolean }
  };

  constructor() {
    super();
    this.columns = [];
    this.rows = [];
    this.contentCm = [];
    this.contentIn = [];
    this.convertToInches = false;
    this.hasInchesData = false;
    this._statusId = `unit_status_${Math.random().toString(36).slice(2)}`;
    this._switchId = `unit_switch_${Math.random().toString(36).slice(2)}`;
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();

    try {
      this.columns = JSON.parse(this.getAttribute('data-columns') || '[]');
      this.rows = JSON.parse(this.getAttribute('data-rows') || '[]');
      this.contentCm = JSON.parse(this.getAttribute('data-content') || '[]');
      this.contentIn = JSON.parse(this.getAttribute('data-content-2') || '[]');
      this.rowHeader = this.getAttribute('data-row-header') || '';

      const savedPref = localStorage.getItem('sizeGuideUnitPreference');

      this.hasInchesData = Array.isArray(this.contentIn) && this.contentIn.length > 0;
      this.convertToInches = savedPref === 'in';

      if (!this.hasInchesData) {
        this.convertToInches = false;
      }

      if (typeof this.columns === 'string') {
        this.columns = this.columns.split(',').map(col => col.trim());
      }
      if (typeof this.rows === 'string') {
        this.rows = this.rows.split(',').map(row => row.trim());
      }

    } catch (e) {
      console.error('Error:', e);
    }
  }

  toggleUnit() {
    if (!this.hasInchesData) return;
  
    this.convertToInches = !this.convertToInches;
    localStorage.setItem('sizeGuideUnitPreference', this.convertToInches ? 'in' : 'cm');
    document.querySelectorAll('size-guide-table').forEach(table => {
      if (table.hasInchesData) {
        table.convertToInches = this.convertToInches;
        table.requestUpdate();
      }
    });
    this.requestUpdate();
  }

  onSwitchKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.toggleUnit();
      e.currentTarget.checked = this.convertToInches;
      e.currentTarget.focus();
    }
  }

  firstUpdated() {
    const table = this.querySelector('.size_guide_table');
    if (!table) return;

    table.addEventListener('mouseover', (event) => {
      const cell = event.target.closest('td, th');
      if (!cell) return;

      // Clear previous hover state for cells
      Array.from(table.querySelectorAll('.hovered-cell')).forEach(c => c.classList.remove('hovered-cell'));

      const rowIndex = cell.parentElement.rowIndex;
      const colIndex = cell.cellIndex;

      // Add classes for row and column hover
      Array.from(table.rows).forEach((row, rIdx) => {
        if (rIdx === rowIndex) {
          row.classList.add('hovered-row');
        } else {
          row.classList.remove('hovered-row');
        }
      });
      
      Array.from(table.querySelectorAll('tr')).forEach(row => {
        Array.from(row.cells).forEach((c, cIdx) => {
          if (cIdx === colIndex) {
            c.classList.add('hovered-column');
          } else {
            c.classList.remove('hovered-column');
          }
        });
      });

      // Add class to the specific hovered cell
      if (cell.tagName === 'TD') {
        cell.classList.add('hovered-cell');
      }
    });

    table.addEventListener('mouseout', () => {
      Array.from(table.querySelectorAll('.hovered-row')).forEach(el => el.classList.remove('hovered-row'));
      Array.from(table.querySelectorAll('.hovered-column')).forEach(el => el.classList.remove('hovered-column'));
      Array.from(table.querySelectorAll('.hovered-cell')).forEach(el => el.classList.remove('hovered-cell'));
    });
  }

  render() {
    return html`
      <div class="size_guide_table_container">
         ${this.hasInchesData ? html`
          <div class="table_switch_container">
            <label class="table_switch" for="${this._switchId}">
              <input
                id="${this._switchId}"
                type="checkbox"
                ?checked="${this.convertToInches}"
                @change="${this.toggleUnit}"
                @keydown="${this.onSwitchKeydown}"
                aria-label="Centimeters or inches displayed"
                aria-describedby="${this._statusId}">
              <span class="table_slider">
                <span>cm</span>
                <span>in.</span>
              </span>
            </label>
            <span id="${this._statusId}" class="sr-only">
              ${this.convertToInches ? 'Currently showing inches' : 'Currently showing centimeters'}
            </span>

          </div>
        ` : ''}
        <div class= "size_guide_table_wrapper">
          <table class="size_guide_table">
            <thead class="size_guide_table_head">
            <tr>
              <!-- Show rowHeader text in the top-left cell -->
              <th class="size_guide_table_header">${this.rowHeader}</th>
              ${this.columns.map(column => html`
                <th class="size_guide_table_header">${column}</th>
              `)}
            </tr>
          </thead>
            <tbody class="size_guide_table_body">
              ${this.rows.map((row, rowIndex) => html`
                <tr class="size_guide_table_row">
                  <th class="size_guide_table_row_header">${row}</th>
                  ${this.hasInchesData && this.convertToInches
                    ? this.contentIn[rowIndex]?.map(value => html`<td class="size_guide_table_cell">${value}</td>`)
                    : this.contentCm[rowIndex]?.map(value => html`<td class="size_guide_table_cell">${value}</td>`)}
                </tr>
              `)}
            </tbody>
          </table>
         </div>
      </div>
    `;
  }
}

customElements.define('size-guide-table', SizeGuideTable);

